// @flow
import { type IntlShape } from 'react-intl';

import type {
  FieldDefinition,
  FieldsListItem,
  MetadataDataType,
  MetadataDefinition,
  MetadataMapping,
  NamespaceDefinition,
} from 'state/types/catalogTypes';
import localizedStrings from 'util/localizedStrings';
import { FIELD_TYPE, Messages } from 'portfolio-shared/util/fieldDefUtils';

// special flags that live in the id field of metadata defs
export const ID_NEW_FIELD_DEF = 'new';
export const ID_DISABLED_FIELD_DEF = 'disabled';

export const DEFAULT_STRING_MAX_LENGTH = 249;
export const DEFAULT_DECIMAL_PRECISION = 2;
export const MAX_STRING_MAX_LENGTH = 2000;
export const MAX_DECIMAL_PRECISION = 8;
export const MAX_SYSTEM_NAME_FIELD_LEN = 255; // this is the length enforced on the server

export const MD_MAPPING_TYPE_EMBED = 'embed';
export const MD_MAPPING_TYPE_EXTRACT = 'extract';

type mdMappingOption = {
  id: string,
  label: string,
};

export const NOT_FOUND = '-1';

export const HIDDEN_FIELD_NAMES = [
  'Bit Depth',
  'Cataloged',
  'Cataloged By',
  'Centroid',
  'Color Mode',
  'com.extensis.portfolio.isPanoramic',
  'CorruptFile',
  'Created',
  'Directory Path',
  'Extension Win',
  'Filename',
  'File Size',
  'Flags',
  'FullyCataloged',
  'Geometry',
  'Height',
  'Horizontal Resolution',
  'Last Modified',
  'Last Updated',
  'Mac Alias',
  'Mac Creator',
  'Mac File Type',
  'Mac Zone',
  'MultipageCount',
  'Number of Bands',
  'Path',
  'PlaceHolder',
  'RID',
  'Short Filename Win',
  'SuggestedKeywords',
  'Thumbnail',
  'Thumbnail Size',
  'Updated By',
  'Vertical Resolution',
  'Volume',
  'Watermarked',
  'WatermarkURL',
  'Width',
];

export const FIELDS_CATEGORY_ALL = 'All';
export const FIELDS_CATEGORY_CUSTOM = 'Custom';
export const FIELDS_CATEGORY_SYSTEM = 'System';
export const FIELDS_CATEGORY_EXIF = 'EXIF';
export const FIELDS_CATEGORY_IPTC = 'IPTC';
export const FIELDS_CATEGORY_MOVIE = 'Movie';
export const FIELDS_CATEGORY_MSOFFICE = 'MSOffice';
export const FIELDS_CATEGORY_GEO = 'GEO';
export const FIELDS_CATEGORY_FONTSENSE = 'FontSense';
export const FIELDS_CATEGORY_XMP = 'XMP';

export const FIELDS_CATEGORIES = [
  { id: FIELDS_CATEGORY_ALL, label: localizedStrings.FIELD_CATEGORY_ALL },
  { id: FIELDS_CATEGORY_CUSTOM, label: localizedStrings.FIELD_CATEGORY_CUSTOM },
  { id: FIELDS_CATEGORY_SYSTEM, label: localizedStrings.FIELD_CATEGORY_SYSTEM },
  { id: FIELDS_CATEGORY_EXIF, label: localizedStrings.FIELD_CATEGORY_EXIF },
  { id: FIELDS_CATEGORY_IPTC, label: localizedStrings.FIELD_CATEGORY_IPTC },
  { id: FIELDS_CATEGORY_MOVIE, label: localizedStrings.FIELD_CATEGORY_MOVIE },
  {
    id: FIELDS_CATEGORY_MSOFFICE,
    label: localizedStrings.FIELD_CATEGORY_OFFICE,
  },
  { id: FIELDS_CATEGORY_GEO, label: localizedStrings.FIELD_CATEGORY_GEO },
  {
    id: FIELDS_CATEGORY_FONTSENSE,
    label: localizedStrings.FIELD_CATEGORY_FONTSENSE,
  },
  { id: FIELDS_CATEGORY_XMP, label: localizedStrings.FIELD_CATEGORY_XMP },
];

export const MD_TYPES = [
  { id: FIELDS_CATEGORY_XMP, label: localizedStrings.FIELD_CATEGORY_XMP },
  { id: FIELDS_CATEGORY_EXIF, label: localizedStrings.FIELD_CATEGORY_EXIF },
  { id: FIELDS_CATEGORY_IPTC, label: localizedStrings.FIELD_CATEGORY_IPTC },
  {
    id: FIELDS_CATEGORY_MSOFFICE,
    label: localizedStrings.FIELD_CATEGORY_OFFICE,
  },
  { id: FIELDS_CATEGORY_MOVIE, label: localizedStrings.FIELD_CATEGORY_MOVIE },
  { id: FIELDS_CATEGORY_GEO, label: localizedStrings.FIELD_CATEGORY_GEO },
];

export const MD_MULTIVALUED_TYPES = [
  {
    id: 'NotMultiValued',
    label:
      localizedStrings.FIELD_METADATA_MULTIVALUED_TYPE_NOT_MULTIVALUED_LABEL,
  },
  {
    id: 'Ordered',
    label: localizedStrings.FIELD_METADATA_MULTIVALUED_TYPE_ORDERED_LABEL,
  },
  {
    id: 'Unordered',
    label: localizedStrings.FIELD_METADATA_MULTIVALUED_TYPE_UNORDERED_LABEL,
  },
];

export const MD_DATATYPES = [
  { id: 'String', label: localizedStrings.FIELD_TYPE_STRING },
  { id: 'Number', label: localizedStrings.FIELD_TYPE_NUMBER },
  { id: 'Decimal', label: localizedStrings.FIELD_TYPE_DECIMAL },
  { id: 'DateTime', label: localizedStrings.FIELD_TYPE_DATETIME },
  { id: 'URL', label: localizedStrings.FIELD_TYPE_URL },
  { id: 'TextBlock', label: localizedStrings.FIELD_TYPE_TEXTBLOCK },
  { id: 'Blob', label: localizedStrings.FIELD_TYPE_BLOB },
  { id: 'Boolean', label: localizedStrings.FIELD_TYPE_BOOLEAN },
  { id: 'Structure', label: localizedStrings.FIELD_TYPE_STRUCTURE },
  { id: 'LangAlt', label: localizedStrings.FIELD_TYPE_LANG_ALT },
];

export function getMetadataNamespaces(
  customXMPNamespaces: NamespaceDefinition[],
  metadata: MetadataDefinition[]
): string[] {
  const result = new Set();
  // add custom namespace tags
  customXMPNamespaces.forEach(namespace => {
    result.add(namespace.namespace);
  });

  // get all tags from all metadata defs
  metadata.forEach(md => {
    const ns = getNamespaceAbbreviation(md.tag);
    ns && result.add(ns);
  });
  return Array.from(result).sort((a, b) => {
    return a.localeCompare(b);
  });
}

function getNamespaceAbbreviation(tag: string): ?string {
  if (tag && tag.indexOf(':') !== -1) {
    return tag.substring(0, tag.indexOf(':'));
  }
}

function isFieldDefMultivaluedForMetadataDef(md: MetadataDefinition) {
  // multivalued and font sense metadatas make multivalued fields
  return md.multivalueType !== 'NotMultiValued' || isFontSenseMetadataDef(md);
}

// Get or create a FieldDef based on the name
// Could be a "real" and enabled FieldDef
// Could be a disabled metadata-based FieldDef
// Can return null
export function fieldDefForFieldsListItemName(
  name: string,
  mds: MetadataDefinition[],
  fds: FieldDefinition[]
): ?FieldDefinition {
  if (!name) {
    return null;
  }

  if (fds) {
    const fd = fds.find(fd => fd.systemName === name);
    if (fd) {
      return fd;
    }
  }

  if (mds) {
    const md = mds.find(m => m.displayName === name);
    if (!md) {
      return null;
    }
    const result: FieldDefinition = {
      defaultValues: [],
      hasPredefinedValues: false,
      id: ID_DISABLED_FIELD_DEF,
      isEmbeddable: md.canEmbed,
      isExtractable: false,
      isHierarchical: false,
      isMultivalued: isFieldDefMultivaluedForMetadataDef(md),
      isSearchable: true,
      isSortable: true,
      isSystem: false,
      localizedName: md.displayName,
      nestedPredefinedValues: [],
      predefinedValues: [],
      systemName: md.displayName,
      type: fieldDefTypeForMetadataDefType(md.dataType),
    };

    if (result.type === FIELD_TYPE.STRING) {
      result.maxLength = DEFAULT_STRING_MAX_LENGTH;
    } else if (result.type === FIELD_TYPE.DECIMAL) {
      result.precision = DEFAULT_DECIMAL_PRECISION;
    }

    return result;
  }

  return null;
}

const mapMetadataDefTypeToFieldDefType = {
  String: FIELD_TYPE.STRING,
  Number: FIELD_TYPE.NUMBER,
  Decimal: FIELD_TYPE.DECIMAL,
  DateTime: FIELD_TYPE.DATETIME,
  URL: FIELD_TYPE.URL,
  TextBlock: FIELD_TYPE.TEXTBLOCK,
  Blob: FIELD_TYPE.STRING,
  Boolean: FIELD_TYPE.BOOLEAN,
  Structure: FIELD_TYPE.STRING,
  LangAlt: FIELD_TYPE.STRING,
};

const CANT_DISABLE_CATEGORIES = [
  FIELDS_CATEGORY_ALL,
  FIELDS_CATEGORY_CUSTOM,
  FIELDS_CATEGORY_SYSTEM,
];

function fieldDefTypeForMetadataDefType(type: MetadataDataType): number {
  return mapMetadataDefTypeToFieldDefType[type];
}

function fieldsListItemForFieldDefCanBeDisabled(
  fieldDef: FieldDefinition,
  metadataDefs: MetadataDefinition[],
  category: string
): boolean {
  const hasCorrespondingMetadataDef = !!metadataDefs.find(
    md => md.displayName === fieldDef.systemName
  );
  return (
    hasCorrespondingMetadataDef && !CANT_DISABLE_CATEGORIES.includes(category)
  );
}

function fieldsListItemForFieldDef(
  fieldDef: FieldDefinition,
  metadataDefs: MetadataDefinition[],
  category: string
): FieldsListItem {
  return {
    canBeDisabled: fieldsListItemForFieldDefCanBeDisabled(
      fieldDef,
      metadataDefs,
      category
    ),
    fieldDef: fieldDef,
    hasCheckbox: !CANT_DISABLE_CATEGORIES.includes(category),
    isEnabled: true,
    name: fieldDef.systemName,
    internalName: fieldDef.systemName,
    type: 'fd',
  };
}

function fieldsListItemForMetadataDef(
  metadataDef: MetadataDefinition,
  fds: FieldDefinition[]
): FieldsListItem {
  const hasFieldDefForMetadataDef = !!fds.find(
    fd => fd.systemName === metadataDef.displayName
  );
  return {
    canBeDisabled: true, // TODO
    hasCheckbox: true, // TODO
    isEnabled: hasFieldDefForMetadataDef,
    metadataDefinition: metadataDef,
    name: metadataDef.displayName,
    internalName: metadataDef.displayName,
    type: 'md',
  };
}

const knownVisibleSystemFieldDefNames = [
  'Color Profile',
  'Profil de couleur',
  'Farbprofil',
  'カラープロファイル',
  'Copyright',
  'Droits d’auteur',
  '著作権',
  'Creator Application',
  'Application du créateur',
  'Autorenanwendung',
  '作成アプリケーション',
  'Description Writer',
  'Rédacteur de description',
  'Autor der Beschreibung',
  '説明作成者',
  'Photoshop Label',
  'Étiquette Photoshop',
  'Photoshop-Beschreibung',
  'Photoshop ラベル',
  'Photoshop Rating',
  'Classement Photoshop',
  'Photoshop-Bewertung',
  'Photoshop レート',
  'Website',
  'Site Web',
  'Web サイト',
];

export function isFieldDefInSystemCategory(fd: FieldDefinition) {
  return fd.isSystem || knownVisibleSystemFieldDefNames.includes(fd.systemName);
}

function isNameInFontSenseCategory(name: string) {
  return name.startsWith('Extensis Font Sense');
}

function isFontSenseMetadataDef(md: MetadataDefinition) {
  return (
    md.tag.startsWith('ExtensisFontSense:') && FIELDS_CATEGORY_XMP === md.type
  );
}

function isFieldDefInCustomCategory(
  fd: FieldDefinition,
  mds: MetadataDefinition[]
) {
  return (
    !isFieldDefInSystemCategory(fd) &&
    !isNameInFontSenseCategory(fd.systemName) &&
    !mds.find(md => md.displayName === fd.systemName)
  );
}

export function isFieldRemovable(
  fd: FieldDefinition,
  mds: MetadataDefinition[]
) {
  return (
    isFieldDefInCustomCategory(fd, mds) ||
    (!isFieldDefInSystemCategory(fd) &&
      !mds.find(md => md.displayName === fd.systemName))
  );
}

export function fieldsListItemsForCategory(
  fieldDefs: FieldDefinition[],
  metadataDefs: MetadataDefinition[],
  category: string
): FieldsListItem[] {
  const filteredFieldDefs: FieldDefinition[] = fieldDefs.filter(
    fd => !HIDDEN_FIELD_NAMES.includes(fd.systemName)
  );

  if (category === FIELDS_CATEGORY_ALL) {
    return filteredFieldDefs.map(fd =>
      fieldsListItemForFieldDef(fd, metadataDefs, category)
    );
  } else if (category === FIELDS_CATEGORY_CUSTOM) {
    return filteredFieldDefs
      .filter(
        fd =>
          isFieldDefInCustomCategory(fd, metadataDefs) &&
          !HIDDEN_FIELD_NAMES.includes(fd.systemName) &&
          !fd.systemName.startsWith('EXIF -') &&
          !fd.systemName.startsWith('IPTC -')
      )
      .map(fd => fieldsListItemForFieldDef(fd, metadataDefs, category));
  } else if (category === FIELDS_CATEGORY_FONTSENSE) {
    return metadataDefs
      .filter(md => isFontSenseMetadataDef(md))
      .map(md => fieldsListItemForMetadataDef(md, filteredFieldDefs));
  } else if (category === FIELDS_CATEGORY_SYSTEM) {
    return filteredFieldDefs
      .filter(
        fd =>
          !HIDDEN_FIELD_NAMES.includes(fd.systemName) &&
          isFieldDefInSystemCategory(fd)
      )
      .map(fd => fieldsListItemForFieldDef(fd, metadataDefs, category));
  } else if (category === FIELDS_CATEGORY_XMP) {
    return metadataDefs
      .filter(md => md.type === category && !isFontSenseMetadataDef(md))
      .map(md => fieldsListItemForMetadataDef(md, fieldDefs));
  } else if (
    category === FIELDS_CATEGORY_IPTC ||
    category === FIELDS_CATEGORY_EXIF
  ) {
    const mdItems = metadataDefs
      .filter(md => md.type === category && !isFontSenseMetadataDef(md))
      .map(md => fieldsListItemForMetadataDef(md, fieldDefs));
    const fdItems = fieldDefs
      .filter(
        fd =>
          fd.systemName.startsWith(category + ' -') &&
          !mdItems.find(md => md.name === fd.systemName)
      )
      .map(fd => fieldsListItemForFieldDef(fd, metadataDefs, category));
    return mdItems.concat(fdItems);
  } else {
    return metadataDefs
      .filter(md => md.type === category)
      .map(md => fieldsListItemForMetadataDef(md, fieldDefs));
  }
}

export function isFieldDisabled(field: FieldDefinition): boolean {
  return !field || field.id === 'disabled';
}

export function canHaveMultipleValues(field: FieldDefinition): boolean {
  switch (field.type) {
    case FIELD_TYPE.STRING:
    case FIELD_TYPE.NUMBER:
    case FIELD_TYPE.DECIMAL:
    case FIELD_TYPE.DATETIME:
      return true;
    default:
      return false;
  }
}

export function canHavePredefinedValues(field: FieldDefinition): boolean {
  switch (field.type) {
    case FIELD_TYPE.STRING:
    case FIELD_TYPE.NUMBER:
    case FIELD_TYPE.DECIMAL:
      return true;
    default:
      return false;
  }
}

export function isDateField(field: FieldDefinition): boolean {
  return field && field.type === FIELD_TYPE.DATETIME;
}

export function getMetadataDef(
  metadataDefId: string,
  metadataDefs: MetadataDefinition[]
): ?MetadataDefinition {
  return metadataDefs && metadataDefs.find(md => md.id === metadataDefId);
}

export function makeDefaultMappingsForFieldDef(
  fd: FieldDefinition,
  mdId: string
): MetadataMapping[] {
  const mappings = [];
  if (fd.isEmbeddable) {
    mappings.push({
      fieldDefId: fd.id,
      metadataDefId: mdId,
      priority: 0,
      type: MD_MAPPING_TYPE_EMBED,
    });
  }

  mappings.push({
    fieldDefId: fd.id,
    metadataDefId: mdId,
    priority: 0,
    type: MD_MAPPING_TYPE_EXTRACT,
  });

  return mappings;
}

function getMappingsForMetadata(
  metadataDefId: string,
  metadataMappings: MetadataMapping[]
): ?(MetadataMapping[]) {
  return (
    metadataMappings &&
    metadataMappings.filter(mm => mm.metadataDefId === metadataDefId)
  );
}

function getMappingsForField(
  fieldDefId: string,
  metadataMappings: MetadataMapping[]
): ?(MetadataMapping[]) {
  return (
    metadataMappings &&
    metadataMappings.filter(mm => mm.fieldDefId === fieldDefId)
  );
}

function getMappingsForFieldAndType(
  fieldDefId: string,
  type: string,
  metadataMappings: MetadataMapping[]
): ?(MetadataMapping[]) {
  const mappings: ?(MetadataMapping[]) = getMappingsForField(
    fieldDefId,
    metadataMappings
  );
  return mappings && mappings.filter(m => m.type === type);
}

export function getFieldsThatEmbedToMetadata(
  metadataDefId: string,
  metadataMappings: MetadataMapping[],
  fieldDefinitions: FieldDefinition[]
): ?(FieldDefinition[]) {
  const mms = getMappingsForMetadata(metadataDefId, metadataMappings);
  const mms_embeds = mms && mms.filter(m => m.type === MD_MAPPING_TYPE_EMBED);
  const fd_ids = mms_embeds && mms_embeds.map(mm => mm.fieldDefId);
  const ret =
    fieldDefinitions &&
    fd_ids &&
    fieldDefinitions.filter(fd => fd_ids.includes(fd.id));
  return ret;
}

export function getEmbedMappingForField(
  fieldDefId: string,
  metadataMappings: MetadataMapping[]
): ?MetadataMapping {
  const mappings: ?(MetadataMapping[]) = getMappingsForFieldAndType(
    fieldDefId,
    MD_MAPPING_TYPE_EMBED,
    metadataMappings
  );
  return mappings && mappings[0];
}

function getExtractMappingsForField(
  fieldDefId: string,
  metadataMappings: MetadataMapping[]
): ?(MetadataMapping[]) {
  return getMappingsForFieldAndType(
    fieldDefId,
    MD_MAPPING_TYPE_EXTRACT,
    metadataMappings
  );
}

export function getEmbedMappingMetadataDef(
  fieldDefId: string,
  metadataDefinitions: MetadataDefinition[],
  metadataMappings: MetadataMapping[]
) {
  const embedMapping: ?MetadataMapping = getEmbedMappingForField(
    fieldDefId,
    metadataMappings
  );
  const embedMetadata: ?MetadataDefinition =
    embedMapping &&
    getMetadataDef(embedMapping.metadataDefId, metadataDefinitions);
  return embedMetadata;
}

export function getEmbedMappingMetadataDefId(
  fieldDefId: string,
  metadataDefinitions: MetadataDefinition[],
  metadataMappings: MetadataMapping[]
) {
  const embedMetadata: ?MetadataDefinition = getEmbedMappingMetadataDef(
    fieldDefId,
    metadataDefinitions,
    metadataMappings
  );
  return (embedMetadata && embedMetadata.id) || NOT_FOUND;
}

export function getEmbeddableMetadataOptions(
  metadataDefs: MetadataDefinition[]
): mdMappingOption[] {
  return [{ id: NOT_FOUND, label: '-' }].concat(
    !metadataDefs
      ? []
      : metadataDefs
          .filter(md => md.canEmbed)
          .map(md => ({ id: md.id, label: md.displayName }))
  );
}

export function getExtractMetadataForField(
  fieldDefId: string,
  metadataMappings: MetadataMapping[],
  metadataDefs: MetadataDefinition[]
): MetadataDefinition[] {
  const extractMappings = getExtractMappingsForField(
    fieldDefId,
    metadataMappings
  );
  const result: MetadataDefinition[] = extractMappings
    ? extractMappings.reduce((result, mm) => {
        const md: ?MetadataDefinition = getMetadataDef(
          mm.metadataDefId,
          metadataDefs
        );
        md && result.push(md);
        return result;
      }, [])
    : [];
  return result.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function displayNameForField(intl: IntlShape, fieldName: string) {
  return Messages[fieldName]
    ? intl.formatMessage(Messages[fieldName])
    : fieldName;
}

export function metadataTypeToLocalizedString(type: string) {
  switch (type) {
    case 'Number':
      return localizedStrings.FIELD_TYPE_NUMBER;
    case 'Decimal':
      return localizedStrings.FIELD_TYPE_DECIMAL;
    case 'DateTime':
      return localizedStrings.FIELD_TYPE_DATETIME;
    case 'String':
      return localizedStrings.FIELD_TYPE_STRING;
    case 'Boolean':
      return localizedStrings.FIELD_TYPE_BOOLEAN;
    case 'LangAlt':
      return localizedStrings.FIELD_TYPE_LANG_ALT;
    case 'Blob':
      return localizedStrings.FIELD_TYPE_BLOB;
    case 'URL':
      return localizedStrings.FIELD_TYPE_URL;
    case 'Structure':
      return localizedStrings.FIELD_TYPE_STRUCTURE;
    case 'TextBlock':
      return localizedStrings.FIELD_TYPE_TEXTBLOCK;
    default:
      return localizedStrings.FIELD_TYPE_UNKNOWN;
  }
}

/** Predefined List utils ported from client */

export function normalizeValue(
  fieldDef: FieldDefinition,
  value: any,
  previousValue: any
) {
  switch (fieldDef.type) {
    case FIELD_TYPE.NUMBER:
      // remove any illegal chars
      value = value.replace(/[^-\d]/g, '');
      // if it doesn't match the pattern return the previous value
      // TODO: this is not the best behavior but we will revisit it later
      return value.match(INTEGER_REGEXP) ? value : previousValue;
    case FIELD_TYPE.DECIMAL:
      // remove any illegal chars
      value = value.replace(/[^-.\d]/g, '');
      // if it doesn't match the pattern return the previous value
      return value.match(
        getDecimalRegExp(fieldDef.precision || DEFAULT_DECIMAL_PRECISION)
      )
        ? value
        : previousValue;
    case FIELD_TYPE.TEXTBLOCK:
      // Remove all control characters except for tab, newline, CR.
      return value.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F-\x9F]/g, '');

    default:
      return value;
  }
}

function getDecimalRegExp(precision: number) {
  return new RegExp(`^-?\\d*\\.?\\d{0,${precision}}$`);
}

const INTEGER_REGEXP = new RegExp('^-?\\d*$');

/**
 * Used to reject character input before a field value is changed as opposed to normalizing
 * input after the value has changed.
 */
export function isCharacterLegal(
  c: string,
  fieldDef: FieldDefinition,
  target: any
) {
  // We're only filtering actual characters here, not things like Tab, Enter, Arrow, etc.
  if (c.length !== 1) {
    return true;
  }

  // Based on the current value and selection/cursor position, compute the expected new value.
  const newValue =
    target.value.substring(0, target.selectionStart) +
    c +
    target.value.substring(target.selectionEnd);

  switch (fieldDef.type) {
    case FIELD_TYPE.NUMBER:
      return INTEGER_REGEXP.test(newValue);
    case FIELD_TYPE.DECIMAL:
      return getDecimalRegExp(
        fieldDef.precision || DEFAULT_DECIMAL_PRECISION
      ).test(newValue);
    case FIELD_TYPE.STRING:
    // fall through
    default:
      return true;
  }
}



// WEBPACK FOOTER //
// ./src/util/fieldsPageUtils.js